import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from './ProtectedRoute';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { SchoolsPage } from '@/pages/SchoolsPage';
import { AccountsPage } from '@/pages/AccountsPage';
import { ModulesPage, ModuleDetailPage } from '@/pages/modules';
import {
  ProductsPage,
  ProductDetailPage,
  ProductFormPage,
  OfferDetailPage,
  OfferFormPage,
  OptionFormPage,
} from '@/pages/products';
import { SchoolYearsPage, SchoolYearFormPage } from '@/pages/school-years';
import { SchoolCreatePage, SchoolEditPage, SchoolDetailPage, SchoolsMapPage } from '@/pages/schools';
import {
  SchoolNetworksPage,
  SchoolNetworkDetailPage,
  SchoolNetworkFormPage,
} from '@/pages/school-networks';
import { WhiteLabelsPage, WhiteLabelFormPage, WhiteLabelDetailPage } from '@/pages/white-labels';
import {
  MigrationsPage,
  MigrationDetailPage,
  MigrationFormPage,
} from '@/pages/migrations';
import { LoginPhotosPage } from '@/pages/login-photos/LoginPhotosPage';
import { LoginPhotoFormPage } from '@/pages/login-photos/LoginPhotoFormPage';
import { LoginPhotosCalendarPage } from '@/pages/login-photos/LoginPhotosCalendarPage';
import { DatabasesPage } from '@/pages/databases';
import { ExportsPage } from '@/pages/exports';
import { AdminPermissionsPage } from '@/pages/admin-permissions';
import { AdministratorsPage, AdministratorFormPage } from '@/pages/administrators';
import { NotificationsPage, NotificationFormPage } from '@/pages/notifications';
import { SearchPage } from '@/pages/search';
import { MigrationV1V2Page } from '@/pages/migration-v1-v2/MigrationV1V2Page';

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'schools',
        element: <SchoolsPage />,
      },
      {
        path: 'schools/map',
        element: <SchoolsMapPage />,
      },
      {
        path: 'schools/new',
        element: <SchoolCreatePage />,
      },
      {
        path: 'schools/:code',
        element: <SchoolDetailPage />,
      },
      {
        path: 'schools/:code/edit',
        element: <SchoolEditPage />,
      },
      // School Networks routes
      {
        path: 'school-networks',
        element: <SchoolNetworksPage />,
      },
      {
        path: 'school-networks/new',
        element: <SchoolNetworkFormPage />,
      },
      {
        path: 'school-networks/:id',
        element: <SchoolNetworkDetailPage />,
      },
      // White Labels routes
      {
        path: 'white-labels',
        element: <WhiteLabelsPage />,
      },
      {
        path: 'white-labels/new',
        element: <WhiteLabelFormPage />,
      },
      {
        path: 'white-labels/:id',
        element: <WhiteLabelDetailPage />,
      },
      {
        path: 'accounts',
        element: <AccountsPage />,
      },
      // Modules routes
      {
        path: 'modules',
        element: <ModulesPage />,
      },
      {
        path: 'modules/:code',
        element: <ModuleDetailPage />,
      },
      // Products routes
      {
        path: 'products',
        element: <ProductsPage />,
      },
      {
        path: 'products/new',
        element: <ProductFormPage />,
      },
      {
        path: 'products/:code',
        element: <ProductDetailPage />,
      },
      {
        path: 'products/:code/edit',
        element: <ProductFormPage />,
      },
      {
        path: 'products/:productCode/offers/new',
        element: <OfferFormPage />,
      },
      {
        path: 'products/:productCode/offers/:offerCode',
        element: <OfferDetailPage />,
      },
      {
        path: 'products/:productCode/offers/:offerCode/edit',
        element: <OfferFormPage />,
      },
      {
        path: 'products/:productCode/options/new',
        element: <OptionFormPage />,
      },
      {
        path: 'products/:productCode/options/:optionId',
        element: <OptionFormPage />,
      },
      // School Years routes
      {
        path: 'school-years',
        element: <SchoolYearsPage />,
      },
      {
        path: 'school-years/new',
        element: <SchoolYearFormPage />,
      },
      {
        path: 'school-years/:id/edit',
        element: <SchoolYearFormPage />,
      },
      // Migrations routes
      {
        path: 'migrations',
        element: <MigrationsPage />,
      },
      {
        path: 'migrations/new',
        element: <MigrationFormPage />,
      },
      {
        path: 'migrations/:id',
        element: <MigrationDetailPage />,
      },
      {
        path: 'migrations/:id/edit',
        element: <MigrationFormPage />,
      },
      // Login Photos routes
      {
        path: 'login-photos',
        element: <LoginPhotosPage />,
      },
      {
        path: 'login-photos/calendar',
        element: <LoginPhotosCalendarPage />,
      },
      {
        path: 'login-photos/new',
        element: <LoginPhotoFormPage />,
      },
      {
        path: 'login-photos/:id/edit',
        element: <LoginPhotoFormPage />,
      },
      // Databases route
      {
        path: 'databases',
        element: <DatabasesPage />,
      },
      // Exports route
      {
        path: 'exports',
        element: <ExportsPage />,
      },
      // Admin Permissions route
      {
        path: 'admin-permissions',
        element: <AdminPermissionsPage />,
      },
      // Administrators routes
      {
        path: 'administrators',
        element: <AdministratorsPage />,
      },
      {
        path: 'administrators/new',
        element: <AdministratorFormPage />,
      },
      {
        path: 'administrators/:id/edit',
        element: <AdministratorFormPage />,
      },
      // Notifications routes
      {
        path: 'notifications',
        element: <NotificationsPage />,
      },
      {
        path: 'notifications/new',
        element: <NotificationFormPage />,
      },
      {
        path: 'notifications/:id/edit',
        element: <NotificationFormPage />,
      },
      // Search route
      {
        path: 'search',
        element: <SearchPage />,
      },
      // Migration V1 → V2 route
      {
        path: 'migration-v1-v2',
        element: <MigrationV1V2Page />,
      },
    ],
  },
]);

export function AppRouter() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
